/**
 * Bulk-delete cascade helper. Walks an array of target tenants
 * sequentially, calling the super-admin delete endpoint for each, and
 * reports back per-tenant results.
 *
 * Sequential by design — each cascade touches users + markets + auth,
 * so 100 in parallel would hammer the same docs. The loop also lets us
 * report progress to the UI live, and lets failures stay isolated to
 * the tenant they belong to (one bad cascade doesn't poison the rest).
 *
 * Pure function — no Vue, no Vuex. The component supplies the axios
 * instance, headers, and callback hooks. Tests mock axios directly.
 *
 * @param {Object} opts
 * @param {Array<{id, company_name?}>} opts.targets - tenants to delete
 * @param {string} opts.apiBaseUrl - e.g. config.api_url
 * @param {Object} opts.headers - already-built auth headers
 * @param {string|null} [opts.reason] - audit reason
 * @param {Function} opts.axiosPost - axios.post-compatible function (url, body, config)
 * @param {Function} [opts.humanizeAxiosError] - error → user-facing string
 * @param {Object} [opts.callbacks]
 * @param {Function} [opts.callbacks.onResult] - per-tenant: (result) => void
 * @param {Function} [opts.callbacks.onProgress] - (done, total) => void
 * @returns {Promise<{ results: Array, okCount: number, failCount: number }>}
 */
export async function runBulkDeleteCascade({
  targets,
  apiBaseUrl,
  headers,
  reason,
  axiosPost,
  humanizeAxiosError = defaultHumanize,
  callbacks = {},
}) {
  const results = [];
  for (let i = 0; i < targets.length; i++) {
    const t = targets[i];
    const isZombie = !t.company_name || !t.company_name.trim();
    const confirmString = isZombie ? 'DELETE' : t.company_name;
    const name = t.company_name || `(zombie ${(t.id || '').substring(0, 8)})`;
    let result;
    try {
      const res = await axiosPost(
        `${apiBaseUrl}super_admin/tenants/${t.id}/delete`,
        { confirm_company_name: confirmString, reason: reason || null },
        { headers },
      );
      const data = (res && res.data && res.data.data) || {};
      result = {
        id: t.id,
        name,
        ok: true,
        message:
          `${data.deleted_users || 0} users` +
          (data.global_users_wiped ? `, ${data.global_users_wiped} fully wiped` : '') +
          (data.market_zips_released ? `, ${data.market_zips_released} ZIPs` : ''),
      };
    } catch (e) {
      result = { id: t.id, name, ok: false, message: humanizeAxiosError(e) };
    }
    results.push(result);
    if (callbacks.onResult) callbacks.onResult(result);
    if (callbacks.onProgress) callbacks.onProgress(i + 1, targets.length);
  }
  const okCount = results.filter(r => r.ok).length;
  const failCount = results.length - okCount;
  return { results, okCount, failCount };
}

function defaultHumanize(e) {
  if (e && e.response && e.response.data && e.response.data.errors) {
    const errs = e.response.data.errors;
    if (Array.isArray(errs)) return errs.join(', ');
    if (typeof errs === 'string') return errs;
  }
  return (e && e.message) || 'Unknown error';
}
